import React, { Component, createRef } from "react";
import Dropzone from "react-dropzone";
import { get as _get, isEmpty as _isEmpty } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { Dropdown, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

import { RenderTableCellValue, RenderTableHeaderValue, showAlertMessage, ShowContentLoader, Select, ConfirmBox, Switch } from "../../resusableComponents/common/formElements";
import ReactDataTable from "../../resusableComponents/common/ReactDataTable";
import { withRouter } from "../../resusableComponents/hoc/withRouter";

import CreateNewClient from "../Admin/UserManagement/component/Client/CreateNewClient";

import { hasAccess } from "../../resusableComponents/hoc/AccessManagement";

import { updateLoadingState } from "../../redux/actions/application";

import { getStrategicAnalysisList, deleteStrategicAnalysis, getStrategicAnalysisCron, saveStrategicAnalysis, getStrategicInsightReportTemplate, uploadStrategicPromptFile, publishStrategicInsightReport } from "../../redux/services/strategicAnalysis";

import { doGetClientNameList } from "../../redux/services/campaignSetup";
import { doGetCampaignListByClient } from "../../redux/services/campaign";

import { tempConstant } from "../../helpers/constants";

const dropzoneRef = createRef();

const MAX_COMPETITORS = 10;

const LINKEDIN_REGEX = /^https?:\/\/(www\.)?linkedin\.com\/.+/i;
const DOMAIN_REGEX = /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+\/?$/;

const _getEmptyCompetitor = () => ({ client_name: "", domain: "", linkedin_url: "" });

class StrategicAnalysis extends Component {
  constructor(props) {
    super(props);

    const { intl } = props;

    this.state = {
      // list
      clientNameList: [],
      analysisList: [],
      totalRecords: 0,
      showTableLoader: true,
      listPayload: {
        page_no: 1,
        page_size: 10,
        orderby: "id",
        order: "desc"
      },

      // add / edit form
      showAddForm: false,
      isEditMode: false,
      editAnalysisId: null,
      selectedClient: null,
      campaignList: [],
      selectedCampaigns: [],
      templateList: [],
      selectedTemplate: null,
      reportName: "",
      additionalPrompt: "",
      competitors: [_getEmptyCompetitor()],
      uploadedFile: null,
      promptFileUrl: "",

      formErrors: {},

      // delete confirm
      showDeleteConfirmBox: false,
      selectedAnalysis: {},

      // add client modal
      showAddClientModal: false,
    };

    this.tableColumns = [
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.id", defaultMessage: "ID" })} />),
        selector: (row) => _get(row, "id", ""),
        cell: (row) => (<RenderTableCellValue className="text-truncate" value={_get(row, "id", "")} tooltip={false} />),
        sortable: true,
        id: "id",
        width: "80px"
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.report_name", defaultMessage: "Report Name" })} />),
        selector: (row) => _get(row, "report_name", ""),
        cell: (row) => (<RenderTableCellValue className="text-truncate" value={_get(row, "report_name", "")} tooltip={true} />),
        sortable: false,
        id: "report_name",
        grow: 1
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.client_name", defaultMessage: "Client Name" })} />),
        selector: (row) => _get(row, "client_name", ""),
        cell: (row) => (<RenderTableCellValue className="text-truncate" value={_get(row, "client_name", "")} tooltip={true} />),
        sortable: false,
        id: "client_name",
        grow: 1
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.competitors", defaultMessage: "Competitors" })} />),
        selector: (row) => {
          try {
            const competitors = typeof _get(row, "competitors", "") === "string" ? JSON.parse(_get(row, "competitors", "[]")) : _get(row, "competitors", []);
            return (competitors || []).map((c) => _get(c, "competitor_name", "")).filter(Boolean).join(", ");
          } catch (e) { return ""; }
        },
        cell: (row) => {
          try {
            const competitors = typeof _get(row, "competitors", "") === "string" ? JSON.parse(_get(row, "competitors", "[]")) : _get(row, "competitors", []);
            const competitorNames = (competitors || []).map((c) => _get(c, "competitor_name", "")).filter(Boolean).join(", ");
            return (<RenderTableCellValue className="text-truncate" value={competitorNames} tooltip={true} />);
          } catch (e) { return (<RenderTableCellValue className="text-truncate" value="" tooltip={false} />); }
        },
        sortable: false,
        id: "competitors",
        grow: 2
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.published", defaultMessage: "Published" })} />),
        sortable: false,
        width: "100px",
        center: "true",
        omit: !hasAccess("analysis", "strategic-analysis", ["publish-report"]),
        cell: (row) => (
          <Switch
            checked={_get(row, "published", _get(row, "is_published", false))}
            onChange={(e) => this._handleTogglePublish(row, e.target.checked)}
          />
        )
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.created_at", defaultMessage: "Created At" })} />),
        selector: (row) => _get(row, "created_at", ""),
        cell: (row) => (<RenderTableCellValue className="text-truncate" value={_get(row, "created_at", "")} tooltip={true} />),
        sortable: true,
        id: "created_at",
        grow: 1
      },
      {
        name: (<RenderTableHeaderValue value={intl.formatMessage({ id: "strategic_analysis.table.action", defaultMessage: "Action" })} />),
        sortable: false,
        width: "80px",
        center: "true",
        cell: (row) => {
          const hasView = hasAccess("analysis", "strategic-analysis", ["view-report"]);
          const hasEdit = hasAccess("analysis", "strategic-analysis", ["edit-report"]);
          const hasDelete = hasAccess("analysis", "strategic-analysis", ["delete-report"]);

          if (!hasView && !hasEdit && !hasDelete) {
            return null;
          }

          return (
            <Dropdown>
              <Dropdown.Toggle as="a" variant="link" className="fw-normal dropdown-toggle-no cursor-pointer">
                <img src={require("../../assets/icons/icon-three-dot.svg").default} className="img-fluid" alt="actions" />
              </Dropdown.Toggle>
              <Dropdown.Menu as="ul" className="dropdown-menu">
                {hasView && (
                  <Link
                    to={`/analysis/view-strategic-analysis-report/${_get(row, "id", "")}`}
                    className="dropdown-item d-flex align-items-center gap-2 waves-effect text-capitalize"
                  >
                    <FormattedMessage id="strategic_analysis.action.view" defaultMessage="View" />
                  </Link>
                )}
                {hasEdit && (
                  <Dropdown.Item
                    className="dropdown-item d-flex align-items-center gap-2 waves-effect text-capitalize"
                    onClick={() => this._fetchStrategicAnalysisCron(row)}
                  >
                    <FormattedMessage id="strategic_analysis.action.edit" defaultMessage="Edit" />
                  </Dropdown.Item>
                )}
                {hasDelete && (
                  <Dropdown.Item
                    className="dropdown-item d-flex align-items-center gap-2 waves-effect text-capitalize text-danger"
                    onClick={() => this.setState({ showDeleteConfirmBox: true, selectedAnalysis: row })}
                  >
                    <FormattedMessage id="strategic_analysis.action.delete" defaultMessage="Delete" />
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          );
        }
      }
    ];
  }

  componentDidMount() {
    this._fetchClientNameList();
    this._fetchStrategicAnalysisList();
  }

  // ─── API calls ────────────────────────────────────────────────────────────────

  _fetchClientNameList = async () => {
    const { updateLoadingState } = this.props;

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await doGetClientNameList({ agency_id: null, dropdown_flag: 1 });

      if (_get(response, "flag", false) === true) {
        this.setState({ clientNameList: _get(response, "data.responseData.client_list", []) });
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while fetching client name list."));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while fetching client name list."));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _fetchCampaignListByClient = async (clientId) => {
    const { updateLoadingState } = this.props;

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await doGetCampaignListByClient({ client_id: clientId });

      if (_get(response, "flag", false) === true) {
        const campaignList = _get(response, "data.responseData", []);
        this.setState({ campaignList });
        return campaignList;
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while fetching campaigns."));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while fetching campaigns."));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
    return [];
  }

  _handleClientCreated = () => {
    // Close the modal and refresh the client dropdown
    this.setState({ showAddClientModal: false });
    this._fetchClientNameList();
  }

  _handleClientChange = (selected) => {
    this.setState({
      selectedClient: selected,
      selectedCampaigns: [],
      campaignList: [],
      selectedTemplate: null,
      templateList: [],
      formErrors: { ...this.state.formErrors, selectedClient: undefined }
    }, () => {
      if (selected && selected.id) {
        this._fetchCampaignListByClient(selected.id);
        this._fetchTemplatesByClient(selected.id);
      }
    });
  }

  _fetchTemplatesByClient = async (clientId) => {
    const { updateLoadingState } = this.props;

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await getStrategicInsightReportTemplate(null);

      if (_get(response, "flag", false) === true) {
        const templateList = _get(response, "data", []);
        this.setState({ templateList });
        return templateList;
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while fetching report templates."));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while fetching report templates."));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
    return [];
  }

  _fetchStrategicAnalysisList = async () => {
    const { updateLoadingState, intl } = this.props;
    const { listPayload } = this.state;

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }
      this.setState({ showTableLoader: true });

      const response = await getStrategicAnalysisList(listPayload);

      if (_get(response, "flag", false) === true) {
        this.setState({
          showTableLoader: false,
          analysisList: _get(response, "data.report_list", []),
          totalRecords: _get(response, "data.page_info[0].total_records", 0)
        });
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "strategic_analysis.error.fetch_list", defaultMessage: "Something went wrong while fetching strategic analysis list." })));
        this.setState({ showTableLoader: false, analysisList: [] });
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", intl.formatMessage({ id: "strategic_analysis.error.fetch_list", defaultMessage: "Something went wrong while fetching strategic analysis list." })));
      this.setState({ showTableLoader: false, analysisList: [] });
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _fetchStrategicAnalysisCron = async (row) => {
    const { updateLoadingState } = this.props;
    const { clientNameList } = this.state;

    const analysisId = _get(row, "id", null);

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await getStrategicAnalysisCron(analysisId);

      if (_get(response, "flag", false) === true) {
        const report = _get(response, "data.report[0]", {});
        const competitorsRes = _get(response, "data.detail", []);

        // Match the client from the dropdown list by client_id (use String comparison to avoid type mismatch)
        const matchedClient = (clientNameList || []).find(
          (c) => String(_get(c, "id", "")) === String(_get(report, "client_id", ""))
        ) || null;

        // Fetch campaigns and templates for this client — use returned values directly
        // to avoid reading stale this.state (setState is async/batched)
        let fetchedCampaignList = [];
        let fetchedTemplateList = [];
        if (matchedClient) {
          fetchedCampaignList = await this._fetchCampaignListByClient(matchedClient.id) || [];
          fetchedTemplateList = await this._fetchTemplatesByClient(matchedClient.id) || [];
        }

        // Match campaigns from campaign_list — may be null, a JSON string, comma-separated string, single value, or an array
        let campaignIds = _get(report, "campaign_list", null);
        console.log("[Edit] raw campaign_list:", campaignIds, "| type:", typeof campaignIds, "| fetchedCampaignList:", fetchedCampaignList);

        // Normalise campaignIds into a flat array of primitive IDs
        if (campaignIds === null || campaignIds === undefined) {
          campaignIds = [];
        } else if (typeof campaignIds === "string") {
          const trimmed = campaignIds.trim();
          if (trimmed === "") {
            campaignIds = [];
          } else {
            // Try JSON parse first (handles "[1,2,3]" or "[\"1\",\"2\"]")
            try {
              const parsed = JSON.parse(trimmed);
              campaignIds = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              // Fall back to comma-separated string (e.g. "1,2,3")
              campaignIds = trimmed.split(",").map(s => s.trim()).filter(Boolean);
            }
          }
        } else if (typeof campaignIds === "number") {
          campaignIds = [campaignIds];
        } else if (!Array.isArray(campaignIds)) {
          campaignIds = [campaignIds];
        }

        // If items are objects with an id property, extract the id
        campaignIds = campaignIds.map(item =>
          (item !== null && typeof item === "object" && item.id !== undefined) ? item.id : item
        );

        console.log("[Edit] normalised campaignIds:", campaignIds);

        // Use String() comparison to avoid numeric vs string ID mismatches
        const matchedCampaigns = campaignIds.length > 0
          ? fetchedCampaignList.filter(c => campaignIds.map(String).includes(String(c.id)))
          : [];

        // Match selected template by template_id (use String comparison to avoid type mismatch)
        const matchedTemplate = fetchedTemplateList.find(
          t => String(t.id) === String(_get(report, "template_id", null))
        ) || null;

        // Map competitors from the new API response structure
        const mappedCompetitors = (competitorsRes || []).map((comp) => ({
          client_name: _get(comp, "competitor_name", ""),
          domain: _get(comp, "competitor_domain", "") || "",
          linkedin_url: _get(comp, "competitor_linkedin_url", "") || ""
        }));

        console.log("[Edit] matchedCampaigns:", matchedCampaigns, "| matchedTemplate:", matchedTemplate);

        this.setState({
          isEditMode: true,
          editAnalysisId: analysisId,
          selectedAnalysis: row,
          selectedClient: matchedClient,
          campaignList: fetchedCampaignList,
          selectedCampaigns: matchedCampaigns,
          templateList: fetchedTemplateList,
          selectedTemplate: matchedTemplate,
          reportName: _get(report, "report_name", ""),
          additionalPrompt: _get(report, "prompt", ""),
          promptFileUrl: _get(report, "prompt_file_url", ""),
          competitors: mappedCompetitors.length > 0 ? mappedCompetitors : [_getEmptyCompetitor()],
          formErrors: {},
          showAddForm: true
        });
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while fetching strategic analysis details."));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while fetching strategic analysis details."));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }


  _saveStrategicAnalysis = async () => {
    const { updateLoadingState, intl } = this.props;
    const { selectedClient, selectedCampaigns, selectedTemplate, reportName, additionalPrompt, competitors, isEditMode, editAnalysisId, promptFileUrl } = this.state;

    if (!this._validateForm()) { return; }

    const savePayload = {
      client_name: _get(selectedClient, "client_name", ""),
      client_id: _get(selectedClient, "id", 0),
      campaign_ids: selectedCampaigns.map(c => c.id),
      template_id: _get(selectedTemplate, "id", null),
      report_name: reportName,
      prompt: additionalPrompt,
      prompt_file_url: (promptFileUrl || ""),
      competitor_json: (competitors || []).map((c) => ({
        competitor_name: _get(c, "client_name", ""),
        competitor_domain: _get(c, "domain", ""),
        competitor_linkedin_url: _get(c, "linkedin_url", "")
      })),
    };

    if (isEditMode && editAnalysisId) {
      savePayload.strategic_analysis_id = editAnalysisId;
    }

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await saveStrategicAnalysis(savePayload);

      if (_get(response, "flag", false) === true) {
        showAlertMessage(intl.formatMessage({ id: "strategic_analysis.success.save", defaultMessage: "Report build has been initiated. You will be informed by email as soon as the report is ready." }), "success");
        this._resetForm();
        this._fetchStrategicAnalysisList();
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "strategic_analysis.error.save", defaultMessage: "Something went wrong while saving analysis." })));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", intl.formatMessage({ id: "strategic_analysis.error.save", defaultMessage: "Something went wrong while saving analysis." })));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _deleteStrategicAnalysis = async () => {
    const { updateLoadingState, intl } = this.props;
    const { selectedAnalysis } = this.state;

    const analysisId = _get(selectedAnalysis, "id", 0);

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await deleteStrategicAnalysis(analysisId);

      if (_get(response, "flag", false) === true) {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "strategic_analysis.success.delete", defaultMessage: "Analysis deleted successfully." })), "success");
        this.setState({ selectedAnalysis: {}, showDeleteConfirmBox: false });
        this._fetchStrategicAnalysisList();
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "strategic_analysis.error.delete", defaultMessage: "Something went wrong while deleting analysis." })));
        this.setState({ selectedAnalysis: {}, showDeleteConfirmBox: false });
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", intl.formatMessage({ id: "strategic_analysis.error.delete", defaultMessage: "Something went wrong while deleting analysis." })));
      this.setState({ selectedAnalysis: {}, showDeleteConfirmBox: false });
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _handleTogglePublish = async (row, isChecked) => {
    const { updateLoadingState, intl } = this.props;

    const reportId = _get(row, "id", 0);
    const payload = {
      report_id: reportId,
      published: isChecked
    };

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await publishStrategicInsightReport(payload);

      if (_get(response, "flag", false) === true) {
        showAlertMessage(intl.formatMessage({ id: "strategic_analysis.success.publish", defaultMessage: "Strategic Analysis report publish status updated." }), "success");
        this._fetchStrategicAnalysisList();
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "strategic_analysis.error.publish", defaultMessage: "Something went wrong while updating publish status." })));
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", intl.formatMessage({ id: "strategic_analysis.error.publish", defaultMessage: "Something went wrong while updating publish status." })));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  handlePaginationChange = (pageNo) => {
    const { listPayload } = this.state;

    this.setState({ listPayload: { ...listPayload, page_no: pageNo } }, () => {
      this._fetchStrategicAnalysisList();
    });
  }

  handleSortChange = (column, sortDirection) => {
    const { listPayload } = this.state;

    this.setState({
      listPayload: { ...listPayload, orderby: _get(column, "id", "id"), order: sortDirection, page_no: 1 }
    }, () => {
      this._fetchStrategicAnalysisList();
    });
  }

  // ─── Form helpers ─────────────────────────────────────────────────────────────

  _validateLinkedinUrl = (url) => {
    return !_isEmpty((url || "").trim()) && LINKEDIN_REGEX.test((url || "").trim());
  }

  _validateDomain = (domain) => {
    return !_isEmpty((domain || "").trim()) && DOMAIN_REGEX.test((domain || "").trim());
  }

  _validateForm = () => {
    const { intl } = this.props;
    const { selectedClient, selectedCampaigns, selectedTemplate, reportName, competitors } = this.state;
    const errors = {};

    // Client required
    if (_isEmpty(selectedClient)) {
      errors.selectedClient = intl.formatMessage({ id: "strategic_analysis.error.client_required", defaultMessage: "Please select a client." });
    }

    // Campaigns required
    if (_isEmpty(selectedCampaigns)) {
      errors.selectedCampaigns = intl.formatMessage({ id: "strategic_analysis.error.campaign_required", defaultMessage: "Please select at least one campaign." });
    }

    // Template required
    if (_isEmpty(selectedTemplate)) {
      errors.selectedTemplate = intl.formatMessage({ id: "strategic_analysis.error.template_required", defaultMessage: "Please select a template." });
    }

    // Report Name required
    if (_isEmpty((reportName || "").trim())) {
      errors.reportName = intl.formatMessage({ id: "strategic_analysis.error.report_name_required", defaultMessage: "Please enter a report name." });
    }

    // At least one competitor required
    if (_isEmpty(competitors)) {
      errors.competitors = intl.formatMessage({ id: "strategic_analysis.error.competitor_required", defaultMessage: "At least one competitor is required." });
    } else {
      const competitorErrors = (competitors || []).map((c, idx) => {
        const rowErr = {};
        if (_isEmpty((_get(c, "client_name", "")).trim())) {
          rowErr.client_name = intl.formatMessage({ id: "strategic_analysis.error.competitor_name_required", defaultMessage: "Competitor name is required." });
        }

        const domainUrl = (_get(c, "domain", "")).trim();
        if (_isEmpty(domainUrl)) {
          rowErr.domain = intl.formatMessage({ id: "strategic_analysis.error.competitor_domain_required", defaultMessage: "Competitor domain is required." });
        } else if (!this._validateDomain(domainUrl)) {
          rowErr.domain = intl.formatMessage({ id: "strategic_analysis.error.competitor_domain_invalid", defaultMessage: "Please enter a valid domain." });
        }

        const liUrl = (_get(c, "linkedin_url", "")).trim();
        if (_isEmpty(liUrl)) {
          rowErr.linkedin_url = intl.formatMessage({ id: "strategic_analysis.error.competitor_linkedin_url_required", defaultMessage: "Competitor LinkedIn URL is required." });
        } else if (!this._validateLinkedinUrl(liUrl)) {
          rowErr.linkedin_url = intl.formatMessage({ id: "strategic_analysis.error.competitor_linkedin_url_invalid", defaultMessage: "Please enter a valid LinkedIn URL for competitor." });
        }
        return rowErr;
      });

      const hasCompetitorErrors = competitorErrors.some((e) => !_isEmpty(e));
      if (hasCompetitorErrors) {
        errors.competitorRows = competitorErrors;
      }
    }

    this.setState({ formErrors: errors });
    return _isEmpty(errors);
  }

  _resetForm = () => {
    this.setState({
      showAddForm: false,
      isEditMode: false,
      editAnalysisId: null,
      selectedClient: null,
      selectedCampaigns: [],
      selectedTemplate: null,
      reportName: "",
      additionalPrompt: "",
      competitors: [_getEmptyCompetitor()],
      uploadedFile: null,
      promptFileUrl: "",

      formErrors: {}
    });
  }

  _onFileDrop = async (acceptedFiles) => {
    const { updateLoadingState } = this.props;

    if (!acceptedFiles || acceptedFiles.length === 0) { return; }

    const file = acceptedFiles[0];
    this.setState({ uploadedFile: file });

    const formData = new FormData();
    formData.append("formfile", file);

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const response = await uploadStrategicPromptFile(formData);

      if (_get(response, "flag", false) === true) {
        const fileUrl = _get(response, "data.url", "");
        this.setState({ promptFileUrl: fileUrl });
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while uploading the prompt file."));
        this.setState({ uploadedFile: null, promptFileUrl: "" });
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while uploading the prompt file."));
      this.setState({ uploadedFile: null, promptFileUrl: "" });
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _handleAddReport = () => {
    this.setState((prev) => ({ showAddForm: !prev.showAddForm, formErrors: {} }));
  }

  _handleCompetitorChange = (index, field, value) => {
    this.setState((prev) => {
      const updated = (prev.competitors || []).map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      );
      return { competitors: updated };
    });
  }

  _handleAddCompetitorRow = () => {
    const { intl } = this.props;
    const { competitors } = this.state;

    if ((competitors || []).length >= MAX_COMPETITORS) {
      showAlertMessage(intl.formatMessage({ id: "strategic_analysis.error.max_competitors", defaultMessage: "You can add a maximum of 10 competitors." }));
      return;
    }
    this.setState((prev) => ({
      competitors: [...(prev.competitors || []), _getEmptyCompetitor()]
    }));
  }

  _handleDeleteCompetitorRow = (index) => {
    this.setState((prev) => ({
      competitors: (prev.competitors || []).filter((_, i) => i !== index)
    }));
  }

  // ─── Render helpers ───────────────────────────────────────────────────────────

  _renderAddReportForm = () => {
    const { intl } = this.props;
    const { clientNameList, selectedClient, campaignList, selectedCampaigns, templateList, selectedTemplate, reportName, additionalPrompt, competitors, formErrors, showAddClientModal, promptFileUrl, uploadedFile } = this.state;

    return (
      <div className="card mb-5">
        <div className="card-body py-5 px-6">

          <div className="row gy-4 mb-5">
            {/* Client dropdown */}
            <div className="col-lg-6">
              <label className="form-label fw-medium">
                <FormattedMessage id="strategic_analysis.form.label.client" defaultMessage="Client" />
                <span className="text-danger ms-1">*</span>
              </label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <Select
                    inputId="strategic-analysis-client-select"
                    className="form-custom-select"
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.select_client", defaultMessage: "Select Client" })}
                    options={(clientNameList || [])}
                    value={(selectedClient || null)}
                    getOptionLabel={(option) => (_get(option, "client_name", ""))}
                    getOptionValue={(option) => (_get(option, "id", null))}
                    onChange={this._handleClientChange}
                    isMulti={false}
                  />
                </div>
                <button
                  id="btn-add-new-client"
                  type="button"
                  className="btn btn-outline-primary btn-sm waves-effect text-nowrap"
                  style={{ marginTop: "2px" }}
                  onClick={() => this.setState({ showAddClientModal: true })}
                  title={intl.formatMessage({ id: "strategic_analysis.btn.add_client", defaultMessage: "Add Client" })}
                >
                  + <FormattedMessage id="strategic_analysis.btn.add_client" defaultMessage="Add Client" />
                </button>
              </div>
              {_get(formErrors, "selectedClient") && (
                <div className="text-danger small mt-1">{_get(formErrors, "selectedClient", "")}</div>
              )}
            </div>

            {/* Add Client Modal */}
            <Modal
              show={(showAddClientModal || false)}
              onHide={() => this.setState({ showAddClientModal: false })}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title className="fw-semibold text-dark-35">
                  <FormattedMessage id="strategic_analysis.modal.add_client_title" defaultMessage="Add New Client" />
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                <CreateNewClient
                  skipAccessCheck={true}
                  onClientCreated={this._handleClientCreated}
                />
              </Modal.Body>
            </Modal>


            {/* Campaigns dropdown */}
            <div className="col-lg-6">
              <label className="form-label fw-medium">
                <FormattedMessage id="strategic_analysis.form.label.campaign" defaultMessage="Campaigns" />
                <span className="text-danger ms-1">*</span>
              </label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <Select
                    inputId="strategic-analysis-campaign-select"
                    className="form-custom-select"
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.select_campaign", defaultMessage: "Select Campaigns" })}
                    options={(campaignList || [])}
                    value={(selectedCampaigns || [])}
                    getOptionLabel={(option) => (_get(option, "name", ""))}
                    getOptionValue={(option) => (_get(option, "id", null))}
                    onChange={(selected) => this.setState({ selectedCampaigns: selected, formErrors: { ...formErrors, selectedCampaigns: undefined } })}
                    isMulti={true}
                    isDisabled={!selectedClient}
                  />
                </div>
              </div>
              {_get(formErrors, "selectedCampaigns") && (
                <div className="text-danger small mt-1">{_get(formErrors, "selectedCampaigns", "")}</div>
              )}
            </div>

            {/* Template dropdown */}
            <div className="col-lg-6">
              <label className="form-label fw-medium">
                <FormattedMessage id="strategic_analysis.form.label.template" defaultMessage="Template" />
                <span className="text-danger ms-1">*</span>
              </label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <Select
                    inputId="strategic-analysis-template-select"
                    className="form-custom-select"
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.select_template", defaultMessage: "Select Template" })}
                    options={(templateList || [])}
                    value={(selectedTemplate || null)}
                    getOptionLabel={(option) => (_get(option, "name", ""))}
                    getOptionValue={(option) => (_get(option, "id", null))}
                    onChange={(selected) => this.setState({ selectedTemplate: selected, formErrors: { ...formErrors, selectedTemplate: undefined } })}
                    isMulti={false}
                  />
                </div>
              </div>
              {_get(formErrors, "selectedTemplate") && (
                <div className="text-danger small mt-1">{_get(formErrors, "selectedTemplate", "")}</div>
              )}
            </div>

            {/* Report Name */}
            <div className="col-lg-6">
              <label className="form-label fw-medium">
                <FormattedMessage id="strategic_analysis.form.label.report_name" defaultMessage="Report Name" />
                <span className="text-danger ms-1">*</span>
              </label>
              <input
                id="strategic-analysis-report-name"
                type="text"
                className={`form-control${_get(formErrors, "reportName") ? " is-invalid" : ""}`}
                placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.report_name", defaultMessage: "Enter report name" })}
                value={(reportName || "")}
                onChange={(e) => this.setState({ reportName: e.target.value, formErrors: { ...formErrors, reportName: undefined } })}
              />
              {_get(formErrors, "reportName") && (
                <div className="invalid-feedback">{_get(formErrors, "reportName", "")}</div>
              )}
            </div>

            {/* Additional Prompt Injection */}
            <div className="col-lg-12">
              <label className="form-label fw-medium">
                <FormattedMessage id="strategic_analysis.form.label.additional_prompt" defaultMessage="Additional Prompt Injection (Optional)" />
              </label>
              <textarea
                id="strategic-analysis-additional-prompt"
                className="form-control"
                placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.additional_prompt", defaultMessage: "Enter any additional context or prompts..." })}
                rows="3"
                value={(additionalPrompt || "")}
                onChange={(e) => this.setState({ additionalPrompt: e.target.value })}
              />
            </div>

            {/* File Upload */}
            <div className="col-lg-12">
              <label className="form-label fw-medium">
                <FormattedMessage id="file.file_upload" defaultMessage="File Upload" />
              </label>

              {/* Show existing file (from edit) or newly uploaded file with remove button */}
              {(!_isEmpty(promptFileUrl) || !_isEmpty(_get(uploadedFile, "name", ""))) ? (
                <div className="position-relative">
                  <div className="form-control d-flex align-items-center justify-content-between">
                    <span className="text-truncate">
                      {_get(uploadedFile, "name", "") || (promptFileUrl || "").split("/").pop()}
                    </span>
                    <button
                      id="btn-remove-prompt-file"
                      type="button"
                      className="btn btn-link p-0 ms-2 text-danger"
                      title={intl.formatMessage({ id: "file.remove", defaultMessage: "Remove file" })}
                      onClick={() => this.setState({ promptFileUrl: "", uploadedFile: null })}
                    >
                      <img src={require("../../assets/icons/icon-close.svg").default} className="img-fluid" alt="remove" style={{ width: "14px", height: "14px" }} />
                    </button>
                  </div>
                </div>
              ) : (
                <Dropzone
                  maxFiles={1}
                  multiple={false}
                  accept={_get(tempConstant, "allowedPromptFiles", {})}
                  onDrop={(f) => this._onFileDrop(f)}
                  ref={dropzoneRef}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className="mb-6" {...getRootProps({ className: "dropzone" })}>
                      <div>
                        <div className="dropzone-input form-control">
                          <span>Browse...</span>
                          No file selected.
                        </div>
                        <input {...getInputProps()} />
                      </div>
                      <label htmlFor="singleUpload" className="mt-2 text-grey-93">
                        <FormattedMessage
                          id="file.allowed_files"
                          defaultMessage="Allowed file extensions : {allowedExtensions}"
                          values={{ allowedExtensions: _get(tempConstant, "allowedPromptFilesLabel", "") }}
                        />
                      </label>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
          </div>

          <hr className="my-4" />

          {/* ── Section: Competitor Selection ── */}
          <h6 className="fw-semibold text-dark-35 text-uppercase mb-4">
            <FormattedMessage id="strategic_analysis.form.section.competitor_selection" defaultMessage="Competitor Selection" />
          </h6>

          {_get(formErrors, "competitors") && (
            <div className="text-danger small mb-3">{_get(formErrors, "competitors", "")}</div>
          )}

          {/* Competitor header row */}
          <div className="row gx-3 mb-2 d-none d-lg-flex">
            <div className="col-lg-4">
              <span className="form-label fw-medium mb-0">
                <FormattedMessage id="strategic_analysis.form.label.competitor_name" defaultMessage="Name" />
                <span className="text-danger ms-1">*</span>
              </span>
            </div>
            <div className="col-lg-3">
              <span className="form-label fw-medium mb-0">
                <FormattedMessage id="strategic_analysis.form.label.domain" defaultMessage="Domain" />
                <span className="text-danger ms-1">*</span>
              </span>
            </div>
            <div className="col-lg-4">
              <span className="form-label fw-medium mb-0">
                <FormattedMessage id="strategic_analysis.form.label.linkedin_url" defaultMessage="LI URL (LinkedIn URL)" />
                <span className="text-danger ms-1">*</span>
              </span>
            </div>
          </div>

          {/* Competitor rows */}
          {(competitors || []).map((competitor, index) => {
            const rowErrors = _get(formErrors, `competitorRows[${index}]`, _get(formErrors, `competitorRows.${index}`, {}));
            return (
              <div key={index} className="row gx-3 mb-3 align-items-start">
                <div className="col-lg-4">
                  <input
                    id={`competitor-name-${index}`}
                    type="text"
                    className={`form-control${_get(rowErrors, "client_name") ? " is-invalid" : ""}`}
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.competitor_name", defaultMessage: "Enter competitor name" })}
                    value={(_get(competitor, "client_name", ""))}
                    onChange={(e) => this._handleCompetitorChange(index, "client_name", e.target.value)}
                  />
                  {_get(rowErrors, "client_name") && (
                    <div className="invalid-feedback">{_get(rowErrors, "client_name", "")}</div>
                  )}
                </div>

                <div className="col-lg-3">
                  <input
                    id={`competitor-domain-${index}`}
                    type="text"
                    className={`form-control${_get(rowErrors, "domain") ? " is-invalid" : ""}`}
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.domain", defaultMessage: "example.com" })}
                    value={(_get(competitor, "domain", ""))}
                    onChange={(e) => this._handleCompetitorChange(index, "domain", e.target.value)}
                  />
                  {_get(rowErrors, "domain") && (
                    <div className="invalid-feedback">{_get(rowErrors, "domain", "")}</div>
                  )}
                </div>

                <div className="col-lg-4">
                  <input
                    id={`competitor-linkedin-url-${index}`}
                    type="text"
                    className={`form-control${_get(rowErrors, "linkedin_url") ? " is-invalid" : ""}`}
                    placeholder={intl.formatMessage({ id: "strategic_analysis.form.placeholder.linkedin_url", defaultMessage: "https://www.linkedin.com/company/..." })}
                    value={(_get(competitor, "linkedin_url", ""))}
                    onChange={(e) => this._handleCompetitorChange(index, "linkedin_url", e.target.value)}
                  />
                  {_get(rowErrors, "linkedin_url") && (
                    <div className="invalid-feedback">{_get(rowErrors, "linkedin_url", "")}</div>
                  )}
                </div>

                <div className="col-lg-1 d-flex align-items-start pt-1">
                  <button
                    id={`btn-delete-competitor-${index}`}
                    type="button"
                    className="btn btn-outline-danger btn-sm waves-effect"
                    onClick={() => this._handleDeleteCompetitorRow(index)}
                  >
                    <FormattedMessage id="strategic_analysis.form.btn.delete_row" defaultMessage="Delete" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add More */}
          {(competitors || []).length < MAX_COMPETITORS && (
            <div className="mb-4">
              <button
                id="btn-add-more-competitor"
                type="button"
                className="btn btn-link p-0 text-primary fw-medium"
                onClick={this._handleAddCompetitorRow}
              >
                <FormattedMessage id="strategic_analysis.btn.add_more" defaultMessage="+ Add More" />
              </button>
            </div>
          )}

          <hr className="my-4" />

          {/* Form action buttons */}
          <div className="d-flex gap-3">
            <button
              id="btn-generate-report"
              type="button"
              className="btn btn-primary waves-effect"
              onClick={this._saveStrategicAnalysis}
            >
              <FormattedMessage id="strategic_analysis.btn.generate_report" defaultMessage="Generate Report" />
            </button>
            <button
              id="btn-cancel-add-report"
              type="button"
              className="btn btn-outline-secondary waves-effect"
              onClick={this._resetForm}
            >
              <FormattedMessage id="strategic_analysis.btn.cancel" defaultMessage="Cancel" />
            </button>
          </div>

        </div>
      </div>
    );
  }

  render() {
    const { intl } = this.props;
    const { showTableLoader, analysisList, totalRecords, showAddForm, showDeleteConfirmBox } = this.state;

    return (
      <>
        <div className="container-fluid flex-grow-1 px-6 py-6">
          <div className="row">
            <div className="col-lg-12 d-flex align-items-center justify-content-between mb-5">
              <h1 className="heading-04 fw-semibold text-dark-35 text-uppercase mb-0">
                <FormattedMessage id="strategic_analysis.title" defaultMessage="Strategic Analysis" />
              </h1>
              {(hasAccess("analysis", "strategic-analysis", ["generate-report"])) && (
                <button
                  id="btn-add-strategic-analysis-report"
                  className="btn btn-primary waves-effect"
                  onClick={this._handleAddReport}
                >
                  <FormattedMessage id="strategic_analysis.btn.add_report" defaultMessage="Add Report" />
                </button>
              )}
            </div>

            {/* Expandable Add Report Form */}
            {showAddForm && this._renderAddReportForm()}

            {/* Analysis List Table */}
            <div className="row mt-5">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body py-5">
                    <div className="table-responsive custom-scroll">
                      <ReactDataTable
                        className="custom-scroll-460"
                        columns={this.tableColumns}
                        data={(analysisList || [])}
                        progressPending={showTableLoader}
                        progressComponent={<ShowContentLoader />}
                        pagination={true}
                        totalRecords={(totalRecords || 0)}
                        onChangePage={(pageNo) => this.handlePaginationChange(pageNo)}
                        onSort={(column, sortDirection) => this.handleSortChange(column, sortDirection)}
                        sortServer={true}
                        defaultSortFieldId="id"
                        defaultSortAsc={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ConfirmBox
          isOpen={(showDeleteConfirmBox || false)}
          onConfirm={() => this._deleteStrategicAnalysis()}
          onClose={() => this.setState({ showDeleteConfirmBox: false, selectedAnalysis: {} })}
          content={intl.formatMessage({ id: "strategic_analysis.confirm.delete", defaultMessage: "Are you sure you want to delete this analysis report?" })}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = (dispatch) => ({
  updateLoadingState: (data) => dispatch(updateLoadingState(data))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(StrategicAnalysis)));
